import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getShopList, getPreferredArea } from '../../../api/commonApi';
import { Shop } from '../../../interface/Shop';
import { defaultAlert } from "../../../utils/alert";
import KakaoMap from '../../../components/kakao/KakaoMap';
import DaumPostcode from 'react-daum-postcode';

export default function NearbyShopPage() {
    const navigate = useNavigate();
    const [preferredArea, setPreferredArea] = useState<string>('');
    const [location, setLocation] = useState('');
    const [searchKeyword, setSearchKeyword] = useState('');
    const [shops, setShops] = useState<Shop[]>([]);
    const [visibleShopIds, setVisibleShopIds] = useState<number[]>([]);
    const [loading, setLoading] = useState(false);
    const [displayCount, setDisplayCount] = useState(5);
    const [showPostcode, setShowPostcode] = useState(false);

    useEffect(() => {
        fetchPreferredArea();
    }, []);

    // 주소검색 팝업 열릴 때 body 스크롤 잠금
    useEffect(() => {
        if (showPostcode) {
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
        } else {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
        }
        return () => {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
        };
    }, [showPostcode]);

    const fetchPreferredArea = async () => {
        try {
            const response = await getPreferredArea();
            const area = response.data.data || '서울특별시 영등포구';
            setPreferredArea(area);
            setLocation(area);
        } catch (error) {
            console.error('선호 지역 조회 실패:', error);
            setPreferredArea('서울특별시 영등포구');
            setLocation('서울특별시 영등포구');
        }
    };

    useEffect(() => {
        if (preferredArea) {
            fetchShops();
        }
    }, [preferredArea]);

    const fetchShops = async (keyword?: string) => {
        try {
            setLoading(true);
            const response = await getShopList({
                location,
                keyword: keyword || ''
            });
            setShops(response.data);
        } catch (error) {
            console.error('Failed to fetch shops:', error);
            defaultAlert('가맹점 정보를 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleVisibleShopsChange = (ids: number[]) => {
        setVisibleShopIds(ids);
        setDisplayCount(5);
    };

    const handleAddressComplete = (data: any) => {
        let fullAddress = data.address;
        let extraAddress = '';

        if (data.addressType === 'R') {
            if (data.bname !== '') {
                extraAddress += data.bname;
            }
            if (data.buildingName !== '') {
                extraAddress += (extraAddress !== '' ? `, ${data.buildingName}` : data.buildingName);
            }
            fullAddress += (extraAddress !== '' ? ` (${extraAddress})` : '');
        }

        setLocation(fullAddress);
        setPreferredArea(fullAddress);
        setShowPostcode(false);
        fetchShops();
    };

    const handleLocationChange = () => {
        setShowPostcode(true);
    };

    const handleSearch = () => {
        if (!searchKeyword.trim()) {
            defaultAlert('검색어를 입력해주세요');
            return;
        }
        setDisplayCount(5);
        fetchShops(searchKeyword);
    };

    const handleLoadMore = () => {
        setDisplayCount(prev => prev + 5);
    };

    // 지도에 보이는 가맹점만 필터링 (visibleShopIds가 비어있으면 전체 표시)
    const visibleShops = visibleShopIds.length > 0
        ? shops.filter(shop => visibleShopIds.includes(shop.id))
        : [];

    const displayShops = visibleShops.slice(0, displayCount);


    return (
        <main className="container type_gray no-bottom-nav">
            {/* 주소 검색 모달 */}
            {showPostcode && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'white',
                        zIndex: 1000,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '12px 16px',
                            borderBottom: '1px solid #e0e0e0',
                            flexShrink: 0,
                        }}
                    >
                        <span style={{ fontSize: '16px', fontWeight: '600' }}>주소 검색</span>
                        <button
                            onClick={() => setShowPostcode(false)}
                            style={{
                                background: 'none',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                padding: '6px 12px',
                                cursor: 'pointer',
                                fontSize: '14px',
                            }}
                        >
                            닫기
                        </button>
                    </div>
                    {/* @ts-ignore */}
                    <DaumPostcode
                        onComplete={handleAddressComplete}
                        style={{ flex: 1, minHeight: 0, height: '100%' }}
                        scriptUrl="https://t1.kakaocdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js"
                    />
                </div>
            )}

            <section className="bg-white">
                <div className="inner">
                    <div className="sort-wrap">
                        <p className="location-add">
                            <i className="fa-solid fa-location-dot"></i>
                            <span className="cont">{location}</span>
                        </p>
                        <button type="button" onClick={handleLocationChange}>위치변경</button>
                    </div>
                </div>
                <div className="mapWrap">
                    {preferredArea && (
                        <KakaoMap
                            initialAddress={preferredArea}
                            shops={shops}
                            onVisibleShopsChange={handleVisibleShopsChange}
                        />
                    )}
                </div>
            </section>

            {/* 검색 영역 */}
            <section className="bg-gray no-gap">
                <div className="inner-box">
                    <form className="form-box" onSubmit={(e) => {
                        e.preventDefault();
                        handleSearch();
                    }}>
                        <fieldset>
                            <legend>검색어 입력</legend>
                            <div className="form-item">
                                <div className="input-box bg-white">
                                    <input
                                        type="text"
                                        value={searchKeyword}
                                        onChange={(e) => setSearchKeyword(e.target.value)}
                                        title="키워드 입력창"
                                        placeholder="검색어를 입력해주세요"
                                    />
                                    <button type="button" className="search-btn" onClick={handleSearch}>
                                        <i className="fa-solid fa-magnifying-glass"></i>
                                        <span className="blind">검색</span>
                                    </button>
                                </div>
                            </div>
                        </fieldset>
                    </form>
                </div>
            </section>

            {/* 결과 영역 */}
            <section className="no-gap">
                <div className="inner">
                    <div className="sort-wrap">
                        <p className="total">
                            총 <span className="point">{visibleShops.length}</span>건이 검색되었습니다.
                        </p>
                    </div>

                    {loading ? (
                        <div style={{textAlign: 'center', padding: '40px 20px'}}>
                            <p>로딩 중...</p>
                        </div>
                    ) : displayShops.length > 0 ? (
                        displayShops.map((shop) => (
                            <div key={shop.id} className="round-box">
                                <div
                                    onClick={() => navigate(`/shop/${shop.id}`)}
                                    style={{cursor: 'pointer'}}
                                >
                                    <div className="place-wrap">
                                        <div className="thumbnail-wrap">
                                            <div className="thumbnail">
                                                <div className="centered">
                                                    <img
                                                        src={shop.shopImageUrl || 'https://via.placeholder.com/100'}
                                                        className="landscape"
                                                        alt={shop.shopName}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="place-wrap-info">
                                            <p className="name">{shop.shopName}</p>
                                            <p className="add">{shop.businessAddress}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div style={{textAlign: 'center', padding: '40px 20px'}}>
                            <p>검색 결과가 없습니다.</p>
                        </div>
                    )}
                </div>

                {displayCount < visibleShops.length && (
                    <div className="inner-box">
                        <button type="button" className="page-more-btn" onClick={handleLoadMore}>
                            더보기 <i className="fa-solid fa-chevron-down"></i>
                        </button>
                    </div>
                )}
            </section>
        </main>
    );
}